import { differenceInCalendarDays, isToday, isYesterday } from "date-fns";
import {
  Bot,
  ChevronDown,
  ChevronRight,
  Mail,
  PencilLine,
  Plus,
  Search,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  useAronConversations,
  useAronPendingWriteThreads,
  useDeleteAronConversation,
} from "../api/aronApi";
import { useAuthStore } from "@/store/auth";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { AronAdminThreadList } from "./AronAdminThreadList";
import type { AronAdminConversation, AronConversation } from "../types";

interface Props {
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  /** Wybór CUDZEJ rozmowy w zakładce „Wszystkie" (audyt CEO+DEV). Bez tego
   * callbacku zakładka się nie pokazuje — np. w pływającym oknie. */
  onSelectAudit?: (conv: AronAdminConversation) => void;
}

type BucketKey = "today" | "yesterday" | "week" | "older";

const BUCKET_ORDER: BucketKey[] = ["today", "yesterday", "week", "older"];

const BUCKET_LABELS: Record<BucketKey, string> = {
  today: "Dziś",
  yesterday: "Wczoraj",
  week: "Ostatnie 7 dni",
  older: "Poprzednie",
};

/** Bucket a conversation by its most recent activity (last message, else creation). */
function bucketOf(c: AronConversation): BucketKey {
  const d = new Date(c.last_message_at ?? c.created_at);
  if (isToday(d)) return "today";
  if (isYesterday(d)) return "yesterday";
  if (differenceInCalendarDays(new Date(), d) <= 7) return "week";
  return "older";
}

export function AronConversationList({
  selectedId,
  onSelect,
  onNew,
  onSelectAudit,
}: Props) {
  // Zakładka „Wszystkie" = wgląd w rozmowy CAŁEJ firmy. Ta sama bramka co przy
  // stronie „Audyt ARON" (CEODevGuard / IsCEOAndDeveloper po stronie serwera):
  // CEO, który jest RÓWNIEŻ developerem.
  const canAudit = useAuthStore(
    (s) => !!s.user?.is_ceo && !!s.user?.is_developer,
  );
  const showAuditTabs = canAudit && !!onSelectAudit;
  const [pane, setPane] = useState<"mine" | "all">("mine");
  const auditing = showAuditTabs && pane === "all";
  // Search runs on the SERVER over every thread of this user — the list is paged,
  // so filtering what is already loaded would hide exactly the old conversation
  // the box exists to find.
  const [term, setTerm] = useState("");
  const query = useDebouncedValue(term.trim(), 300);
  const searching = query.length > 0;
  const {
    data: convs = [],
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAronConversations(query);
  const del = useDeleteAronConversation();
  // Conversations are an audit trail — only the CEO can delete them.
  const canDelete = useAuthStore((s) => !!s.user?.is_ceo);

  // The conversation list is paginated (first page only), so a write proposal
  // buried in an old thread would be invisible while still counted by the
  // sidebar badge. This unpaginated set mirrors the badge exactly — every
  // counted proposal has a clickable row in the panel below.
  const { data: pendingThreads = [] } = useAronPendingWriteThreads();

  // Only "Dziś" and "Wczoraj" expanded by default; a bucket holding a thread
  // with pending write proposals starts expanded until the user toggles it.
  const [toggled, setToggled] = useState<Partial<Record<BucketKey, boolean>>>({});
  const DEFAULT_COLLAPSED: Record<BucketKey, boolean> = {
    today: false,
    yesterday: false,
    week: true,
    older: true,
  };

  const groups = useMemo(() => {
    const m: Record<BucketKey, AronConversation[]> = {
      today: [],
      yesterday: [],
      week: [],
      older: [],
    };
    for (const c of convs) m[bucketOf(c)].push(c);
    return m;
  }, [convs]);

  // "Pokaż starsze" is an explicit request to see old threads — the bucket they
  // land in must not swallow them behind its default-collapsed header.
  const loadMore = () => {
    setToggled((t) => ({ ...t, week: false, older: false }));
    void fetchNextPage();
  };

  const isBucketCollapsed = (key: BucketKey) =>
    toggled[key] ??
    (groups[key].some((c) => (c.pending_writes_count ?? 0) > 0)
      ? false
      : DEFAULT_COLLAPSED[key]);

  const renderItem = (c: AronConversation) => (
    <div
      key={c.id}
      className={`group flex items-center gap-2 border-l-[3px] px-2 py-2 ${
        selectedId === c.id
          ? c.autopilot
            ? "border-black bg-gray-200 dark:border-white dark:bg-gray-800"
            : "border-red-600 bg-red-50 dark:bg-red-900/20"
          : "border-transparent hover:bg-gray-200/60 dark:hover:bg-gray-800"
      }`}
    >
      <button
        type="button"
        onClick={() => onSelect(c.id)}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        {c.is_generating ? (
          <span
            // `rounded-full` is what the global brutalist "radius: 0" rule
            // whitelists for spinners — without it this renders as a square
            // tumbling on its corner.
            className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-red-500 border-t-transparent"
            title="ARON generuje odpowiedź…"
            aria-label="generuje"
          />
        ) : c.autopilot ? (
          <Zap
            size={14}
            className="shrink-0 text-black dark:text-white"
            aria-label="tryb YOLO"
          />
        ) : (
          <Bot size={14} className="shrink-0 text-red-500" />
        )}
        <span className="min-w-0 flex-1 truncate text-xs text-gray-700 dark:text-gray-200">
          {c.title || "Nowa rozmowa"}
        </span>
        {(c.pending_writes_count ?? 0) > 0 && (
          <span
            title={`${c.pending_writes_count} ${
              c.pending_writes_count === 1
                ? "propozycja zapisu czeka"
                : "propozycje zapisu czekają"
            } na Twoje zatwierdzenie`}
            className="inline-flex shrink-0 items-center gap-0.5 bg-red-600 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-white"
          >
            <PencilLine size={10} /> {c.pending_writes_count}
          </span>
        )}
      </button>
      {canDelete && (
        <button
          type="button"
          onClick={() => del.mutate(c.id)}
          title="Usuń rozmowę"
          className="text-gray-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-gray-100 dark:bg-gray-950">
      <div className="p-3">
        <button
          type="button"
          onClick={onNew}
          className="flex w-full items-center justify-center gap-1.5 bg-red-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
        >
          <Plus size={15} /> Nowa rozmowa
        </button>
        {showAuditTabs && (
          <div className="mt-2 flex border border-gray-300 dark:border-gray-700">
            {(
              [
                ["mine", "Moje"],
                ["all", "Wszystkie"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPane(key)}
                aria-pressed={pane === key}
                title={
                  key === "mine"
                    ? "Twoje rozmowy z ARON-em"
                    : "Rozmowy wszystkich pracowników — podgląd tylko do odczytu, każde wejście zapisuje się w rejestrze dostępów (RODO)"
                }
                className={`flex-1 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                  pane === key
                    ? "bg-red-600 text-white"
                    : "text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-800"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {!auditing && (
        <div className="relative mt-2">
          <Search
            size={13}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            // Not type="search": Chrome then paints its OWN clear cross next to
            // the one below (two ✕ in a 200 px box).
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Szukaj w rozmowach…"
            aria-label="Szukaj w rozmowach"
            className="w-full border border-gray-300 bg-white py-1.5 pl-7 pr-7 text-xs text-gray-700 placeholder:text-gray-400 focus:border-red-500 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
          />
          {term && (
            <button
              type="button"
              onClick={() => setTerm("")}
              title="Wyczyść"
              aria-label="Wyczyść wyszukiwanie"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X size={13} />
            </button>
          )}
        </div>
        )}
      </div>
      {auditing && onSelectAudit && (
        <AronAdminThreadList selectedId={selectedId} onSelect={onSelectAudit} />
      )}
      {!auditing && pendingThreads.length > 0 && (
        <div className="mx-2 mb-2 border border-red-300 bg-red-50 p-2 dark:border-red-900/50 dark:bg-red-900/15">
          <div className="mb-1 flex items-center gap-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
            <PencilLine size={11} /> Czekają na Twoją decyzję
          </div>
          <div className="max-h-52 overflow-y-auto">
          {pendingThreads.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelect(t.id)}
              title={
                t.source === "mail"
                  ? "Propozycja z rozmowy mailowej (Poczta ARON)"
                  : "Otwórz rozmowę i zatwierdź lub odrzuć propozycję"
              }
              className={`flex w-full items-center gap-1.5 px-1.5 py-1 text-left text-xs text-red-800 hover:bg-red-100 dark:text-red-200 dark:hover:bg-red-900/30 ${
                selectedId === t.id ? "bg-red-100 dark:bg-red-900/30" : ""
              }`}
            >
              {t.source === "mail" ? (
                <Mail size={11} className="shrink-0" />
              ) : (
                <Bot size={11} className="shrink-0" />
              )}
              <span className="min-w-0 flex-1 truncate">
                {t.title || "Nowa rozmowa"}
              </span>
              <span className="shrink-0 bg-red-600 px-1.5 font-mono text-[10px] font-semibold text-white">
                {t.pending_writes_count}
              </span>
            </button>
          ))}
          </div>
        </div>
      )}
      {!auditing && (
      <div className="flex-1 overflow-y-auto px-2 pb-2">
        {isLoading ? (
          <div className="p-3 text-sm text-gray-400">
            {searching ? "Szukam…" : "Ładowanie…"}
          </div>
        ) : convs.length === 0 ? (
          <div className="p-3 text-sm text-gray-400">
            {searching
              ? `Brak rozmów pasujących do „${query}".`
              : "Brak rozmów. Zacznij nową."}
          </div>
        ) : searching ? (
          // Hits come from the whole history — a collapsed date bucket would make
          // a found thread look like no result at all, so results render flat.
          <div className="mb-1">
            <div className="flex items-center gap-1 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
              <span>Znalezione</span>
              <span className="ml-auto font-normal text-gray-300 dark:text-gray-600">
                {convs.length}
              </span>
            </div>
            {convs.map(renderItem)}
          </div>
        ) : (
          BUCKET_ORDER.map((key) => {
            const items = groups[key];
            if (items.length === 0) return null;
            const isCollapsed = isBucketCollapsed(key);
            return (
              <div key={key} className="mb-1">
                <button
                  type="button"
                  onClick={() =>
                    setToggled((s) => ({ ...s, [key]: !isCollapsed }))
                  }
                  className="flex w-full items-center gap-1 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {isCollapsed ? (
                    <ChevronRight size={13} className="shrink-0" />
                  ) : (
                    <ChevronDown size={13} className="shrink-0" />
                  )}
                  <span>{BUCKET_LABELS[key]}</span>
                  <span className="ml-auto font-normal text-gray-300 dark:text-gray-600">
                    {items.length}
                  </span>
                </button>
                {!isCollapsed && items.map(renderItem)}
              </div>
            );
          })
        )}
        {hasNextPage && !isLoading && (
          <button
            type="button"
            onClick={loadMore}
            disabled={isFetchingNextPage}
            className="mt-1 w-full border border-gray-300 px-2 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {isFetchingNextPage ? "Ładowanie…" : "Pokaż starsze"}
          </button>
        )}
      </div>
      )}
    </div>
  );
}
